import { useEffect, useMemo, useRef, useState } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';
// IMPORTANT: import the worker via Vite's ?url so it is bundled from the
// SAME pdfjs-dist that react-pdf resolves at runtime. Loading from a CDN
// or a separate top-level resolution can cause "API version X does not
// match Worker version Y" errors when nested deps drift.
import pdfWorkerUrl from 'pdfjs-dist/build/pdf.worker.min.mjs?url';
import {
  ChevronLeft,
  ChevronRight,
  ZoomIn,
  ZoomOut,
  Search,
  Download,
  Loader2,
  FileWarning,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import { api } from '@/lib/api';

pdfjs.GlobalWorkerOptions.workerSrc = pdfWorkerUrl;

interface PdfViewerProps {
  documentId: string;
  fileName?: string;
  className?: string;
  /** Optional: scroll to this page (1-based) when it changes. */
  highlightPage?: number;
  /**
   * Optional: an opaque key that changes on every "scroll request" the parent
   * makes. Lets the parent re-fire scroll-to-page when the page number is the
   * same as last time (e.g. clicking the same citation twice).
   */
  jumpKey?: number | string;
  /** Optional: text to highlight/search in the PDF. */
  highlightText?: string;
}

export function PdfViewer({
  documentId,
  fileName,
  className,
  highlightPage,
  jumpKey,
  highlightText,
}: PdfViewerProps) {
  const [fileData, setFileData] = useState<Uint8Array | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [numPages, setNumPages] = useState<number>(0);
  const [page, setPage] = useState(1);
  const [scale, setScale] = useState(1.1);
  const [search, setSearch] = useState('');
  const [searchOpen, setSearchOpen] = useState(false);
  const pagesRef = useRef<Record<number, HTMLDivElement | null>>({});
  const scrollerRef = useRef<HTMLDivElement | null>(null);

  // Fetch PDF bytes (with Bearer auth) and hand to react-pdf as a Uint8Array
  useEffect(() => {
    let cancelled = false;
    setFileData(null);
    setLoadError(null);
    (async () => {
      try {
        const res = await api.get(`/api/documents/${documentId}/file`, {
          responseType: 'arraybuffer',
        });
        if (cancelled) return;
        setFileData(new Uint8Array(res.data));
      } catch (err) {
        if (cancelled) return;
        const msg = err instanceof Error ? err.message : 'Failed to load document';
        setLoadError(msg);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [documentId]);

  // Memoise the file prop — react-pdf reloads on every reference change
  const file = useMemo(() => (fileData ? { data: fileData } : null), [fileData]);

  // Jump to highlightPage when requested. jumpKey re-fires the effect even
  // when the page number is unchanged (e.g. clicking the same citation twice).
  useEffect(() => {
    if (!highlightPage) return;
    setPage(highlightPage);
    const el = pagesRef.current[highlightPage];
    if (el && scrollerRef.current) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [highlightPage, jumpKey]);

  // Apply highlightText from props (one-shot)
  useEffect(() => {
    if (highlightText && highlightText.trim()) {
      setSearch(highlightText.trim());
      setSearchOpen(true);
    }
  }, [highlightText]);

  function gotoPage(p: number) {
    const clamped = Math.max(1, Math.min(numPages || 1, p));
    setPage(clamped);
    const el = pagesRef.current[clamped];
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  async function downloadFile() {
    try {
      const res = await api.get(`/api/documents/${documentId}/file`, { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName ?? 'document.pdf';
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      // swallow — shown via toast upstream if the user retries
    }
  }

  const searchLower = search.toLowerCase();

  function customTextRenderer({ str }: { str: string }): string {
    if (!searchLower) return str;
    const idx = str.toLowerCase().indexOf(searchLower);
    if (idx === -1) return str;
    const before = str.slice(0, idx);
    const hit = str.slice(idx, idx + search.length);
    const after = str.slice(idx + search.length);
    return `${escapeHtml(before)}<mark class="bg-amber-300/70 text-foreground">${escapeHtml(hit)}</mark>${escapeHtml(after)}`;
  }

  return (
    <div className={cn('flex h-full flex-col overflow-hidden rounded-lg border bg-muted/10', className)}>
      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-2 border-b bg-background px-3 py-2">
        <div className="flex items-center gap-1">
          <Button
            size="sm"
            variant="ghost"
            onClick={() => gotoPage(page - 1)}
            disabled={!numPages || page <= 1}
            className="h-7 w-7 p-0"
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <div className="flex items-center gap-1 text-xs">
            <Input
              type="number"
              value={page}
              onChange={(e) => gotoPage(Number(e.target.value) || 1)}
              className="h-7 w-14 px-1.5 text-center text-xs"
              min={1}
              max={numPages || 1}
            />
            <span className="text-muted-foreground">/ {numPages || '—'}</span>
          </div>
          <Button
            size="sm"
            variant="ghost"
            onClick={() => gotoPage(page + 1)}
            disabled={!numPages || page >= numPages}
            className="h-7 w-7 p-0"
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>

        <div className="mx-1 h-5 w-px bg-border" />

        <div className="flex items-center gap-1">
          <Button
            size="sm"
            variant="ghost"
            onClick={() => setScale((s) => Math.max(0.5, s - 0.15))}
            className="h-7 w-7 p-0"
          >
            <ZoomOut className="h-4 w-4" />
          </Button>
          <span className="min-w-[3rem] text-center text-xs tabular-nums text-muted-foreground">
            {Math.round(scale * 100)}%
          </span>
          <Button
            size="sm"
            variant="ghost"
            onClick={() => setScale((s) => Math.min(3, s + 0.15))}
            className="h-7 w-7 p-0"
          >
            <ZoomIn className="h-4 w-4" />
          </Button>
        </div>

        <div className="mx-1 h-5 w-px bg-border" />

        <button
          onClick={() => setSearchOpen((o) => !o)}
          className={cn(
            'flex h-7 items-center gap-1.5 rounded px-2 text-xs transition-colors',
            searchOpen ? 'bg-accent text-foreground' : 'text-muted-foreground hover:bg-accent hover:text-foreground',
          )}
        >
          <Search className="h-3.5 w-3.5" /> Find
        </button>
        {searchOpen && (
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search in document..."
            className="h-7 w-60 text-xs"
            autoFocus
          />
        )}

        <div className="flex-1" />
        <Button size="sm" variant="ghost" onClick={downloadFile} className="h-7 gap-1.5 text-xs">
          <Download className="h-3.5 w-3.5" /> Download
        </Button>
      </div>

      {/* Viewport */}
      <div ref={scrollerRef} className="flex-1 overflow-auto bg-muted/40 p-3">
        {loadError ? (
          <div className="flex h-full items-center justify-center">
            <div className="flex flex-col items-center gap-2 text-center">
              <FileWarning className="h-8 w-8 text-destructive" />
              <div className="text-sm font-medium">Couldn't load document</div>
              <div className="max-w-md text-xs text-muted-foreground">{loadError}</div>
            </div>
          </div>
        ) : !file ? (
          <div className="flex h-full items-center justify-center">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <Document
            file={file}
            onLoadSuccess={({ numPages: n }) => {
              setNumPages(n);
              setPage((p) => Math.min(p, n));
            }}
            onLoadError={(err) => setLoadError(err?.message ?? 'Failed to render PDF')}
            loading={
              <div className="flex h-full items-center justify-center">
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              </div>
            }
            className="flex flex-col items-center gap-3"
          >
            {Array.from({ length: numPages }).map((_, i) => {
              const pageNumber = i + 1;
              return (
                <div
                  key={pageNumber}
                  ref={(el) => {
                    pagesRef.current[pageNumber] = el;
                  }}
                  className={cn(
                    'bg-white shadow-sm ring-1 ring-border',
                    page === pageNumber && 'ring-primary/50',
                  )}
                >
                  <Page
                    pageNumber={pageNumber}
                    scale={scale}
                    renderAnnotationLayer
                    renderTextLayer
                    customTextRenderer={customTextRenderer}
                  />
                </div>
              );
            })}
          </Document>
        )}
      </div>
    </div>
  );
}

function escapeHtml(str: string): string {
  return str
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}
